import React, { useEffect, useState, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { getProduits, createProduit, bulkCreateProduits, updateProduit, deleteProduit, uploadImage, getImages, getStockByEntrepot } from '../services/api';
import {
  Plus, Edit2, Trash2, Search, Loader, Save, Package,
  X, AlertCircle, CheckCircle, Upload, Image as ImageIcon,
  FileSpreadsheet, Download, SlidersHorizontal, ChevronUp, ChevronDown, ChevronsUpDown,
  Eye, Tag, Barcode, Warehouse, AlignLeft, Hash
} from 'lucide-react';
import { useBoutique } from '../context/BoutiqueContext';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { PRODUITS_COLONNES, colonnesVisiblesConfig } from '../constants/produitsColonnes';
import BoutiqueSelector from '../components/BoutiqueSelector';
import './ProduitsFeveo.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0 }).format(n || 0);

const ALL_COLUMNS = PRODUITS_COLONNES;

const getSortValue = (p, key) => {
  const prix = p.typeVente === 'gros' ? (p.prixVenteGros || p.prixVente || 0) : (p.prixVenteDetail || p.prixVente || 0);
  const achat = p.typeVente === 'gros' ? (p.prixAchatGros || p.prixAchat || 0) : (p.prixAchatDetail || p.prixAchat || 0);
  switch (key) {
    case 'produit':     return (p.nom || '').toLowerCase();
    case 'type':        return p.typeVente || '';
    case 'prixAchat':   return achat;
    case 'prixVente':   return prix;
    case 'stock':       return p.quantiteStock || 0;
    case 'statut':      return p.quantiteStock <= p.seuilAlerte ? 0 : 1;
    case 'fournisseur': return (p.fournisseur || '').toLowerCase();
    case 'codeBarres':  return (p.codeBarres || '').toLowerCase();
    case 'description': return (p.description || '').toLowerCase();
    default:            return '';
  }
};

const ProduitsFeveo = () => {
  const { user } = useAuth();
  const { selectedBoutiqueId, boutiques } = useBoutique();
  const [produits,          setProduits]          = useState([]);
  const [filteredProduits,  setFilteredProduits]  = useState([]);
  const [showModal,         setShowModal]         = useState(false);
  const [editingProduit,    setEditingProduit]    = useState(null);
  const [searchTerm,        setSearchTerm]        = useState('');
  const [loading,           setLoading]           = useState(true);
  const [submitting,        setSubmitting]        = useState(false);
  const [toast,             setToast]             = useState({ type: '', text: '' });
  const [imageFile,         setImageFile]         = useState(null);
  const [imagePreview,      setImagePreview]      = useState(null);
  const [showImageLibrary,  setShowImageLibrary]  = useState(false);
  const [imageLibrary,      setImageLibrary]      = useState([]);
  const [uploadingImage,    setUploadingImage]    = useState(false);
  const [showStockModal,    setShowStockModal]    = useState(false);
  const [selectedProduitStock, setSelectedProduitStock] = useState(null);
  const [stockDistribution, setStockDistribution] = useState(null);
  const [loadingDistribution, setLoadingDistribution] = useState(false);

  // Colonnes & tri
  // Colonnes visibles : défaut défini dans Configuration, modifiable ponctuellement ici
  const { config } = useConfig();
  const colonnesConfig = config?.produits_colonnes_visibles;
  const colonnesDefaut = useMemo(() => colonnesVisiblesConfig(colonnesConfig), [colonnesConfig]);
  const [visibleCols,  setVisibleCols]  = useState(colonnesDefaut);
  useEffect(() => { setVisibleCols(colonnesDefaut); }, [colonnesDefaut]);
  const [sortConfig,   setSortConfig]   = useState({ key: 'produit', dir: 'asc' });
  const [showColMenu,  setShowColMenu]  = useState(false);
  const colMenuRef = useRef(null);

  // Vue détail produit
  const [viewProduit, setViewProduit] = useState(null);
  // Lightbox image (clic sur une image produit → affichage plein écran)
  const [lightbox,    setLightbox]    = useState(null);

  // Sélection multiple
  const [selectedIds,    setSelectedIds]    = useState(new Set());
  const [deletingBulk,   setDeletingBulk]   = useState(false);

  const [formData, setFormData] = useState({
    nom: '', codeProduit: '', codeBarres: '',
    typeVente: 'detail',
    prixAchatGros: '', prixVenteGros: '',
    prixAchatDetail: '', prixVenteDetail: '',
    nombrePiecesParCarton: '1', quantiteStock: '0',
    seuilAlerte: '10', fournisseur: '', description: '',
    image: '', boutiqueIdModal: 'all',
    conditionnements: [],
    champsPersonnalises: [],
  });
  const [newCond,  setNewCond]  = useState({ nom: '', quantiteBase: '', prixVente: '', prixAchat: '', prixAchatManual: false, prixVenteManual: false });
  const [newChamp, setNewChamp] = useState({ cle: '', valeur: '' });
  const [boutiquePrices, setBoutiquePrices] = useState({});

  // Import Excel
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview,   setImportPreview]   = useState([]);
  const [importing,       setImporting]       = useState(false);
  const [importProgress,  setImportProgress]  = useState({ step: '', done: 0, total: 0 });
  const [importResult,    setImportResult]    = useState(null);
  const [importError,     setImportError]     = useState('');
  const importFileRef = useRef(null);

  useEffect(() => { loadData(); }, [selectedBoutiqueId]); // eslint-disable-line
  useEffect(() => { filterProducts(); }, [searchTerm, produits, selectedBoutiqueId]); // eslint-disable-line
  useEffect(() => {
    const handler = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast({ type: '', text: '' }), 3500);
  };

  const loadData = async () => {
    try {
      const pr = await getProduits();
      setProduits(pr.data.data);
    } catch { showToast('error', 'Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const filterProducts = () => {
    let f = produits;
    if (selectedBoutiqueId && selectedBoutiqueId !== 'all')
      f = f.filter(p => p.boutiqueId === selectedBoutiqueId);
    if (searchTerm)
      f = f.filter(p => p.nom.toLowerCase().includes(searchTerm.toLowerCase()) || p.codeProduit.toLowerCase().includes(searchTerm.toLowerCase()));
    setFilteredProduits(f);
  };

  const handleShowStockDistribution = async (produit) => {
    setSelectedProduitStock(produit); setShowStockModal(true); setLoadingDistribution(true);
    try { const r = await getStockByEntrepot(produit._id); setStockDistribution(r.data.data); }
    catch { showToast('error', 'Erreur distribution'); setStockDistribution(null); }
    finally { setLoadingDistribution(false); }
  };

  const closeStockModal = () => { setShowStockModal(false); setSelectedProduitStock(null); setStockDistribution(null); };

  const generateCodeProduit = (nom) => {
    if (!nom) return '';
    return `${nom.substring(0,3).toUpperCase().replace(/[^A-Z]/g,'X')}-${Date.now().toString().slice(-6)}`;
  };
  const generateCodeBarres = () => Date.now().toString().slice(-12) + Math.floor(Math.random()*10);

  const handleNomChange = (nom) => {
    if (editingProduit) {
      setFormData({ ...formData, nom });
      return;
    }
    setFormData({ ...formData, nom, codeProduit: generateCodeProduit(nom), codeBarres: generateCodeBarres() });
  };

  const handleAddClick = () => {
    resetForm();
    setFormData(prev => ({ ...prev, boutiqueIdModal: selectedBoutiqueId || 'all' }));
    setShowModal(true);
  };

  const handleOpenImport = () => {
    setImportPreview([]); setImportResult(null); setImportError('');
    if (importFileRef.current) importFileRef.current.value = '';
    setShowImportModal(true);
  };

  const downloadTemplate = () => {
    const headers = ['Nom','Code Produit','Code Barres','Boutique','Prix Achat','Prix Vente','Stock Initial','Seuil Alerte','Fournisseur','Description','Image URL'];
    const examples = [
      ['Coca-Cola 50cl','CC001','1234567890123','Boutique A',450,600,100,10,'SODACO','','https://example.com/coca.jpg'],
      ['Pain 200g','P001','','',100,150,200,20,'','Pain artisanal',''],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...examples]);
    ws['!cols'] = headers.map((_,i) => ({ wch: i===10?35:i<2?20:i<4?16:13 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produits');
    XLSX.writeFile(wb, 'modele_import_produits.xlsx');
  };

  // ── Export Excel du catalogue ──
  // On exporte ce que l'utilisateur a sous les yeux (recherche, boutique, tri),
  // ou uniquement sa sélection quand il y en a une.
  const exporterProduits = () => {
    const aExporter = selectedIds.size > 0
      ? sortedProduits.filter(p => selectedIds.has(p._id))
      : sortedProduits;
    if (!aExporter.length) { showToast('error', 'Aucun produit à exporter'); return; }

    const nomBoutique = (id) => boutiques.find(b => String(b._id) === String(id))?.nom || '';
    const statut = (p) => (p.quantiteStock <= 0 ? 'Rupture'
      : p.quantiteStock <= p.seuilAlerte ? 'Stock faible' : 'En stock');

    const colonnes = [
      'Code produit', 'Nom', 'Code-barres', 'Boutique', 'Type de vente',
      'Prix achat détail', 'Prix vente détail', 'Prix achat gros', 'Prix vente gros',
      'Pièces / carton', 'Stock', 'Seuil alerte', 'Statut',
      'Valeur stock (achat)', 'Valeur stock (vente)',
      'Fournisseur', 'Description', 'Conditionnements', 'Champs personnalisés', 'Image',
    ];

    const lignes = aExporter.map(p => {
      const stock = p.quantiteStock || 0;
      // Le prix retenu pour valoriser le stock est celui du mode de vente du produit.
      const achat = p.typeVente === 'gros' ? (p.prixAchatGros || p.prixAchat || 0) : (p.prixAchatDetail || p.prixAchat || 0);
      const vente = p.typeVente === 'gros' ? (p.prixVenteGros || p.prixVente || 0) : (p.prixVenteDetail || p.prixVente || 0);
      return [
        p.codeProduit || '',
        p.nom || '',
        p.codeBarres || '',
        nomBoutique(p.boutiqueId),
        p.typeVente === 'gros' ? 'Gros' : 'Détail',
        p.prixAchatDetail ?? p.prixAchat ?? '',
        p.prixVenteDetail ?? p.prixVente ?? '',
        p.prixAchatGros ?? '',
        p.prixVenteGros ?? '',
        p.nombrePiecesParCarton || 1,
        stock,
        p.seuilAlerte ?? '',
        statut(p),
        stock * achat,
        stock * vente,
        p.fournisseur || '',
        p.description || '',
        (p.conditionnements || []).map(c => `${c.nom} (x${c.quantiteBase} — ${c.prixVente} F)`).join(' | '),
        (p.champsPersonnalises || []).map(c => `${c.cle} : ${c.valeur}`).join(' | '),
        p.image || '',
      ];
    });

    const entete = [
      ['Catalogue produits'],
      [`Export du ${new Date().toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}`],
      [`${aExporter.length} produit(s)`
        + (selectedIds.size > 0 ? ' — sélection en cours' : '')
        + (searchTerm ? ` — recherche « ${searchTerm} »` : '')],
      [],
    ];

    const totaux = Array(colonnes.length).fill('');
    totaux[1]  = 'TOTAUX';
    totaux[10] = lignes.reduce((s, l) => s + (Number(l[10]) || 0), 0);
    totaux[13] = lignes.reduce((s, l) => s + (Number(l[13]) || 0), 0);
    totaux[14] = lignes.reduce((s, l) => s + (Number(l[14]) || 0), 0);

    const ws = XLSX.utils.aoa_to_sheet([...entete, colonnes, ...lignes, [], totaux]);
    ws['!cols'] = [
      { wch:16 }, { wch:34 }, { wch:16 }, { wch:18 }, { wch:13 },
      { wch:16 }, { wch:16 }, { wch:15 }, { wch:15 },
      { wch:14 }, { wch:9 }, { wch:12 }, { wch:13 },
      { wch:18 }, { wch:18 },
      { wch:20 }, { wch:34 }, { wch:34 }, { wch:28 }, { wch:34 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Produits');
    const jour = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `catalogue-produits-${jour}.xlsx`);
    showToast('success', `${aExporter.length} produit(s) exporté(s)`);
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImportError(''); setImportResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
        if (rows.length < 2) { setImportError('Le fichier est vide.'); return; }
        const parsed = [];
        for (let i = 1; i < rows.length; i++) {
          const [nom,codeProduit,codeBarres,boutique,prixAchat,prixVente,stock,seuilAlerte,fournisseur,description,imageUrl] = rows[i].map(v => String(v).trim());
          if (!nom) continue;
          if (!prixAchat || !prixVente) { setImportError(`Ligne ${i+1} : Prix Achat et Prix Vente requis.`); return; }
          parsed.push({ nom,codeProduit,codeBarres,boutique, prixAchat:parseFloat(prixAchat)||0, prixVente:parseFloat(prixVente)||0, stock:parseInt(stock,10)||0, seuilAlerte:parseInt(seuilAlerte,10)||10, fournisseur,description, imageUrl:imageUrl||'' });
        }
        if (!parsed.length) { setImportError('Aucune ligne valide (Nom requis).'); return; }
        setImportPreview(parsed);
      } catch { setImportError("Impossible de lire le fichier."); }
    };
    reader.readAsBinaryString(file);
  };

  const handleImport = async () => {
    if (!importPreview.length) return;
    setImporting(true); setImportError('');
    setImportProgress({ step: 'Préparation des données...', done: 0, total: importPreview.length });

    // Étape 1 : construire les payloads
    setImportProgress(p => ({ ...p, step: 'Construction des produits...', done: 0 }));
    const payloads = [];
    const erreursPrep = [];

    for (let i = 0; i < importPreview.length; i++) {
      const row = importPreview[i];
      setImportProgress(p => ({ ...p, done: i + 1 }));

      let boutiqueId = null;
      if (row.boutique) {
        const bf = boutiques.find(b => b.nom.toLowerCase() === row.boutique.toLowerCase());
        if (!bf) { erreursPrep.push(`Ligne ${i + 2} "${row.nom}" — Boutique "${row.boutique}" introuvable`); continue; }
        boutiqueId = bf._id;
      }

      const payload = {
        nom: row.nom,
        codeBarres: row.codeBarres || '',
        typeVente: 'detail',
        prixAchatDetail: row.prixAchat,
        prixVenteDetail: row.prixVente,
        prixAchat: row.prixAchat,
        prixVente: row.prixVente,
        quantiteStock: row.stock,
        seuilAlerte: row.seuilAlerte,
        fournisseur: row.fournisseur || '',
        description: row.description || '',
        ...(row.codeProduit && { codeProduit: row.codeProduit }),
        ...(row.imageUrl && { image: row.imageUrl }),
        ...(boutiqueId && { boutiqueId }),
      };
      payloads.push(payload);
    }

    // Si tout a été filtré côté client → ne pas appeler l'API, afficher les raisons.
    if (payloads.length === 0) {
      setImportResult({ created: 0, skipped: 0, errors: erreursPrep.length ? erreursPrep : ['Aucune ligne valide à importer.'] });
      setImporting(false);
      setImportProgress({ step: '', done: 0, total: 0 });
      return;
    }

    // Étape 3 : envoi bulk en une seule requête
    try {
      setImportProgress({ step: `Envoi de ${payloads.length} produit(s) au serveur...`, done: payloads.length, total: payloads.length });
      const res = await bulkCreateProduits(payloads);
      const { created, doublons, details } = res.data;
      const allErrors = [
        ...erreursPrep,
        ...(details || []).map(e => `Ligne ${e.ligne || '?'} "${e.nom}" — ${e.erreur}`)
      ];
      setImportResult({ created, skipped: doublons || 0, errors: allErrors });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Erreur serveur';
      // On garde les erreurs de préparation visibles même quand le serveur rejette
      setImportError(msg);
      if (erreursPrep.length) {
        setImportResult({ created: 0, skipped: 0, errors: erreursPrep });
      }
    } finally {
      setImporting(false);
      setImportProgress({ step: '', done: 0, total: 0 });
      loadData();
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveImage = () => { setImageFile(null); setImagePreview(null); setFormData({ ...formData, image: '' }); };
  const loadImageLibrary = async () => {
    try { const { data } = await getImages(); setImageLibrary(data.data); setShowImageLibrary(true); }
    catch { showToast('error', 'Erreur bibliothèque'); }
  };
  const selectImageFromLibrary = (url) => { setFormData({ ...formData, image: url }); setImagePreview(url); setShowImageLibrary(false); };

  const handleEditClick = async (produit) => {
    setEditingProduit(produit);
    setFormData({ nom:produit.nom, codeProduit:produit.codeProduit, codeBarres:produit.codeBarres||'', typeVente:produit.typeVente||'detail', prixAchatGros:produit.prixAchatGros||0, prixVenteGros:produit.prixVenteGros||0, prixAchatDetail:produit.prixAchatDetail||produit.prixAchat||0, prixVenteDetail:produit.prixVenteDetail||produit.prixVente||0, nombrePiecesParCarton:produit.nombrePiecesParCarton||'1', quantiteStock:produit.quantiteStock, seuilAlerte:produit.seuilAlerte, fournisseur:produit.fournisseur||'', description:produit.description||'', image:produit.image||'', conditionnements: produit.conditionnements || [], champsPersonnalises: produit.champsPersonnalises || [] });
    setNewCond({ nom:'', quantiteBase:'', prixVente:'', prixAchat:'', prixAchatManual:false, prixVenteManual:false });
    setNewChamp({ cle:'', valeur:'' });
    if (produit.image) setImagePreview(produit.image);
    setShowModal(true);
  };

  const initBoutiquePrices = (df = formData) => {
    const init = {};
    boutiques.forEach(b => { init[b._id] = { prixAchatDetail:df.prixAchatDetail||'', prixVenteDetail:df.prixVenteDetail||'', prixAchatGros:df.prixAchatGros||'', prixVenteGros:df.prixVenteGros||'' }; });
    setBoutiquePrices(init);
  };
  const applyDefaultPriceToAll = () => {
    const upd = {};
    boutiques.forEach(b => { upd[b._id] = { prixAchatDetail:formData.prixAchatDetail||'', prixVenteDetail:formData.prixVenteDetail||'', prixAchatGros:formData.prixAchatGros||'', prixVenteGros:formData.prixVenteGros||'' }; });
    setBoutiquePrices(upd);
  };
  const setBoutiquePrice = (boutiqueId, field, value) => setBoutiquePrices(prev => ({ ...prev, [boutiqueId]: { ...prev[boutiqueId], [field]: value } }));

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      let imageUrl = formData.image;
      if (imageFile) { setUploadingImage(true); const fd2 = new FormData(); fd2.append('image', imageFile); const ur = await uploadImage(fd2); imageUrl = ur.data.data.url; setUploadingImage(false); }
      const productData = { ...formData, image: imageUrl };
      delete productData.boutiqueIdModal;
      if (editingProduit) {
        await updateProduit(editingProduit._id, productData);
        showToast('success', 'Produit modifié avec succès');
      } else if (formData.boutiqueIdModal === 'all') {
        let created = 0; const errors = [];
        for (const b of boutiques) {
          const prices = boutiquePrices[b._id] || {};
          const payload = { ...productData, boutiqueId: b._id, prixAchatDetail:prices.prixAchatDetail||formData.prixAchatDetail, prixVenteDetail:prices.prixVenteDetail||formData.prixVenteDetail, prixAchatGros:prices.prixAchatGros||formData.prixAchatGros, prixVenteGros:prices.prixVenteGros||formData.prixVenteGros };
          if (payload.typeVente === 'gros') { payload.prixAchat = payload.prixAchatGros||0; payload.prixVente = payload.prixVenteGros||0; }
          else { payload.prixAchat = payload.prixAchatDetail||0; payload.prixVente = payload.prixVenteDetail||0; }
          try { await createProduit(payload); created++; }
          catch (err) { errors.push(`${b.nom} : ${err.response?.data?.message||'Erreur'}`); }
        }
        if (errors.length) showToast('error', `${created} boutique(s), erreurs : ${errors.join(' | ')}`);
        else showToast('success', `Produit créé pour ${created} boutique(s)`);
      } else {
        if (formData.boutiqueIdModal) productData.boutiqueId = formData.boutiqueIdModal;
        const res = await createProduit(productData);
        showToast('success', res.data.count > 1 ? `Produit créé pour ${res.data.count} boutiques` : 'Produit créé');
      }
      setShowModal(false); resetForm(); setBoutiquePrices({}); setImageFile(null); setImagePreview(null); loadData();
    } catch (err) { showToast('error', err.response?.data?.message || 'Une erreur est survenue'); }
    finally { setSubmitting(false); setUploadingImage(false); }
  };

  const resetForm = () => {
    setFormData({ nom:'',codeProduit:'',codeBarres:'',typeVente:'detail',prixAchatGros:'',prixVenteGros:'',prixAchatDetail:'',prixVenteDetail:'',nombrePiecesParCarton:'1',quantiteStock:'0',seuilAlerte:'10',fournisseur:'',description:'',image:'',boutiqueIdModal:'all',conditionnements:[],champsPersonnalises:[] });
    setNewCond({ nom:'', quantiteBase:'', prixVente:'', prixAchat:'', prixAchatManual:false, prixVenteManual:false });
    setNewChamp({ cle:'', valeur:'' });
    setEditingProduit(null); setImageFile(null); setImagePreview(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try { await deleteProduit(id); showToast('success', 'Produit supprimé'); loadData(); }
    catch { showToast('error', 'Erreur lors de la suppression'); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedProduits.length && sortedProduits.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedProduits.map(p => p._id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedIds.size} produit${selectedIds.size > 1 ? 's' : ''} sélectionné${selectedIds.size > 1 ? 's' : ''} ?`)) return;
    setDeletingBulk(true);
    let ok = 0, fail = 0;
    for (const id of selectedIds) {
      try { await deleteProduit(id); ok++; }
      catch { fail++; }
    }
    setSelectedIds(new Set());
    if (fail === 0) showToast('success', `${ok} produit${ok > 1 ? 's' : ''} supprimé${ok > 1 ? 's' : ''}`);
    else showToast('error', `${ok} supprimé${ok > 1 ? 's' : ''}, ${fail} erreur${fail > 1 ? 's' : ''}`);
    setDeletingBulk(false);
    loadData();
  };

  const nbAlertes = produits.filter(p => p.quantiteStock <= p.seuilAlerte).length;

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  const toggleCol = (key) => {
    setVisibleCols(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Memo: ne re-sorte que si la liste filtrée ou le tri changent.
  // Évite de re-trier 500+ produits à chaque frappe dans le modal.
  const sortedProduits = useMemo(() => {
    return [...filteredProduits].sort((a, b) => {
      const va = getSortValue(a, sortConfig.key);
      const vb = getSortValue(b, sortConfig.key);
      if (va < vb) return sortConfig.dir === 'asc' ? -1 : 1;
      if (va > vb) return sortConfig.dir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [filteredProduits, sortConfig.key, sortConfig.dir]);

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <ChevronsUpDown size={11} className="pf-sort-icon pf-sort-icon--idle"/>;
    return sortConfig.dir === 'asc'
      ? <ChevronUp   size={11} className="pf-sort-icon pf-sort-icon--active"/>
      : <ChevronDown size={11} className="pf-sort-icon pf-sort-icon--active"/>;
  };

  if (loading) return (
    <div className="pf-loading"><Loader className="pf-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="pf-page">

      {/* Toast */}
      {toast.text && (
        <div className={`pf-toast pf-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="pf-header">
        <div className="pf-header-left">
          <div className="pf-header-icon"><Package size={20}/></div>
          <div>
            <h1 className="pf-header-title">Catalogue Produits</h1>
            <p className="pf-header-sub">Gérez vos produits, tarifs et niveaux de stock</p>
          </div>
        </div>
        <div className="pf-header-right">
          {['admin-entreprise','super-admin'].includes(user?.role) && <BoutiqueSelector/>}
          <button className="pf-btn-outline" onClick={exporterProduits} disabled={!sortedProduits.length}
            title="Exporter en Excel les produits affichés (ou la sélection)">
            <Download size={13}/> Exporter{selectedIds.size > 0 ? ` (${selectedIds.size})` : ' Excel'}
          </button>
          {['admin-entreprise','admin-pro','admin-boutique','gestionnaire','super-admin'].includes(user?.role) && (
            <>
              <button className="pf-btn-outline" onClick={handleOpenImport}>
                <FileSpreadsheet size={13}/> Importer Excel
              </button>
              <button className="pf-cta-btn" onClick={handleAddClick}>
                <Plus size={14}/> Nouveau produit
              </button>
            </>
          )}
        </div>
      </div>

      <div className="pf-content">

        {/* KPI row */}
        <div className="pf-kpi-row">
          {[
            { label: 'Total produits',   value: produits.length,          color: '#6366f1', bg: '#eef2ff', icon: Package },
            { label: 'Alertes stock',    value: nbAlertes,                color: '#ef4444', bg: '#fef2f2', icon: AlertCircle },
            { label: 'Résultats',        value: filteredProduits.length,  color: '#10b981', bg: '#ecfdf5', icon: Search },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="pf-kpi" style={{ borderTopColor: k.color }}>
                <div className="pf-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={15}/></div>
                <div className="pf-kpi-val">{k.value}</div>
                <div className="pf-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Barre de sélection groupée */}
        {selectedIds.size > 0 && (
          <div className="pf-bulk-bar">
            <span className="pf-bulk-info">
              <CheckCircle size={14}/>
              <strong>{selectedIds.size}</strong> produit{selectedIds.size > 1 ? 's' : ''} sélectionné{selectedIds.size > 1 ? 's' : ''}
            </span>
            <div className="pf-bulk-actions">
              <button className="pf-bulk-clear" onClick={() => setSelectedIds(new Set())}>
                <X size={13}/> Désélectionner
              </button>
              <button className="pf-bulk-del" onClick={handleBulkDelete} disabled={deletingBulk}>
                {deletingBulk
                  ? <><Loader size={13} className="pf-spin"/> Suppression…</>
                  : <><Trash2 size={13}/> Supprimer {selectedIds.size > 1 ? `les ${selectedIds.size}` : 'le'} produit{selectedIds.size > 1 ? 's' : ''}</>}
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="pf-filters">
          <div className="pf-search-wrap">
            <Search size={14} className="pf-search-ico"/>
            <input className="pf-search-input" placeholder="Rechercher un produit ou code…"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
            {searchTerm && (
              <button className="pf-search-clear" onClick={() => setSearchTerm('')}><X size={13}/></button>
            )}
          </div>
          {/* Sélecteur de colonnes */}
          <div className="pf-col-menu-wrap" ref={colMenuRef}>
            <button
              className={`pf-col-btn ${showColMenu ? 'pf-col-btn--open' : ''}`}
              onClick={() => setShowColMenu(v => !v)}
              title="Personnaliser les colonnes">
              <SlidersHorizontal size={13}/>
              Colonnes
              <span className="pf-col-badge">{visibleCols.size}</span>
            </button>
            {showColMenu && (
              <div className="pf-col-dropdown">
                <div className="pf-col-dropdown-head">Colonnes visibles</div>
                {ALL_COLUMNS.map(col => (
                  <label key={col.key} className={`pf-col-option ${col.always ? 'pf-col-option--disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={visibleCols.has(col.key)}
                      disabled={col.always}
                      onChange={() => !col.always && toggleCol(col.key)}/>
                    {col.label}
                    {col.always && <span className="pf-col-always">fixe</span>}
                  </label>
                ))}
                <div className="pf-col-dropdown-foot">
                  <button className="pf-col-reset" onClick={() => setVisibleCols(colonnesDefaut)} title="Revenir aux colonnes définies dans Configuration">
                    Réinitialiser
                  </button>
                </div>
              </div>
            )}
          </div>

          <span className="pf-count">{filteredProduits.length} résultat{filteredProduits.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Table */}
        {filteredProduits.length === 0 ? (
          <div className="pf-empty">
            <div className="pf-empty-icon"><Package size={30}/></div>
            <h3>{searchTerm ? 'Aucun résultat' : 'Aucun produit'}</h3>
            <p>{searchTerm ? 'Essayez d\'autres filtres' : 'Commencez par créer votre premier produit'}</p>
            {!searchTerm && (
              <button className="pf-cta-btn" onClick={handleAddClick}><Plus size={13}/> Créer un produit</button>
            )}
          </div>
        ) : (
          <div className="pf-card">
            <table className="pf-table">
              <thead>
                <tr>
                  <th className="pf-th-check">
                    <input
                      type="checkbox"
                      className="pf-checkbox"
                      checked={sortedProduits.length > 0 && selectedIds.size === sortedProduits.length}
                      ref={el => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < sortedProduits.length; }}
                      onChange={toggleSelectAll}
                      title="Tout sélectionner / désélectionner"/>
                  </th>
                  {ALL_COLUMNS.filter(c => visibleCols.has(c.key)).map(col => (
                    <th key={col.key}
                      className="pf-th-sortable"
                      onClick={() => handleSort(col.key)}>
                      {col.label} <SortIcon col={col.key}/>
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedProduits.map(p => {
                  const isLow  = p.quantiteStock <= p.seuilAlerte;
                  const prix   = p.typeVente === 'gros' ? (p.prixVenteGros  || p.prixVente || 0) : (p.prixVenteDetail || p.prixVente || 0);
                  const achat  = p.typeVente === 'gros' ? (p.prixAchatGros  || p.prixAchat || 0) : (p.prixAchatDetail || p.prixAchat || 0);
                  const col      = (key) => visibleCols.has(key);
                  const isSelected = selectedIds.has(p._id);
                  return (
                    <tr key={p._id} className={isSelected ? 'pf-row--selected' : ''}>
                      <td className="pf-td-check" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="pf-checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(p._id)}/>
                      </td>
                      {col('produit') && (
                        <td>
                          <div className="pf-prod-cell">
                            {p.image
                              ? <img src={p.image} alt={p.nom}
                                  className="pf-prod-img pf-prod-img--clickable"
                                  loading="lazy" decoding="async"
                                  title="Cliquer pour agrandir"
                                  onClick={() => setLightbox({ url: p.image, nom: p.nom, code: p.codeProduit })}
                                  onError={e => e.target.style.display='none'}/>
                              : <div className="pf-prod-img pf-prod-img--placeholder"><Package size={14}/></div>}
                            <div>
                              <div className="pf-prod-name">{p.nom}</div>
                              <div className="pf-prod-code">{p.codeProduit}</div>
                            </div>
                          </div>
                        </td>
                      )}
                      {col('type') && (
                        <td>
                          {p.conditionnements?.length > 0
                            ? <span className="pf-cat-pill">{p.conditionnements.length} cond.</span>
                            : <span className="pf-muted">—</span>}
                        </td>
                      )}
                      {col('prixAchat') && (
                        <td className="pf-price pf-price--achat">{fmt(achat)}</td>
                      )}
                      {col('prixVente') && (
                        <td className="pf-price">{fmt(prix)}</td>
                      )}
                      {col('stock') && (
                        <td>
                          <button className={`pf-stock-badge ${isLow ? 'pf-stock-badge--low' : 'pf-stock-badge--ok'}`}
                            onClick={() => handleShowStockDistribution(p)}
                            title="Voir la distribution par entrepôt">
                            {p.quantiteStock} u
                          </button>
                        </td>
                      )}
                      {col('statut') && (
                        <td>
                          <span className={`pf-status-badge ${isLow ? 'pf-status-badge--low' : 'pf-status-badge--ok'}`}>
                            {isLow ? 'Stock bas' : 'Disponible'}
                          </span>
                        </td>
                      )}
                      {col('fournisseur') && (
                        <td className="pf-muted">{p.fournisseur || '—'}</td>
                      )}
                      {col('codeBarres') && (
                        <td><span className="pf-barcode-cell">{p.codeBarres || '—'}</span></td>
                      )}
                      {col('description') && (
                        <td className="pf-desc-cell">{p.description
                          ? <span title={p.description}>{p.description.length > 60 ? p.description.slice(0,60) + '…' : p.description}</span>
                          : <span className="pf-muted">—</span>}
                        </td>
                      )}
                      <td>
                        <div className="pf-actions">
                          <button className="pf-ico-btn pf-ico-btn--view" onClick={() => setViewProduit(p)} title="Voir le produit">
                            <Eye size={13}/>
                          </button>
                          {['admin-entreprise','admin-pro','admin-boutique','gestionnaire','super-admin'].includes(user?.role) && (
                            <button className="pf-ico-btn pf-ico-btn--edit" onClick={() => handleEditClick(p)} title="Modifier">
                              <Edit2 size={13}/>
                            </button>
                          )}
                          {['admin-entreprise','admin-boutique','super-admin'].includes(user?.role) && (
                            <button className="pf-ico-btn pf-ico-btn--del" onClick={() => handleDelete(p._id)} title="Supprimer">
                              <Trash2 size={13}/>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════
          Modal Produit (Create / Edit)
         ══════════════════════════════════════ */}
      {showModal && (
        <>
          <div className="pf-overlay" onClick={() => { setShowModal(false); resetForm(); }}/>
          <div className="pf-modal pf-modal--wide">
            <div className="pf-modal-head">
              <div>
                <h2 className="pf-modal-title">{editingProduit ? 'Modifier le produit' : 'Nouveau produit'}</h2>
                <p className="pf-modal-sub">{editingProduit ? editingProduit.nom : 'Renseigner les informations du produit'}</p>
              </div>
              <button className="pf-modal-close" onClick={() => { setShowModal(false); resetForm(); }}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit} className="pf-modal-body">

              {/* Boutique selector */}
              {!editingProduit && ['admin-entreprise','super-admin'].includes(user?.role) && boutiques.length > 0 && (
                <div className="pf-field pf-field--full">
                  <label>Boutique *</label>
                  <select value={formData.boutiqueIdModal} required
                    onChange={e => {
                      const val = e.target.value;
                      setFormData({ ...formData, boutiqueIdModal: val });
                      if (val === 'all') {
                        const init = {};
                        boutiques.forEach(b => { init[b._id] = { prixAchatDetail:formData.prixAchatDetail||'', prixVenteDetail:formData.prixVenteDetail||'', prixAchatGros:formData.prixAchatGros||'', prixVenteGros:formData.prixVenteGros||'' }; });
                        setBoutiquePrices(init);
                      }
                    }}>
                    <option value="all">🏪 Toutes mes boutiques ({boutiques.length})</option>
                    {boutiques.map(b => <option key={b._id} value={b._id}>{b.nom}</option>)}
                  </select>
                </div>
              )}

              {/* Section: Identification */}
              <div className="pf-section-label">Identification</div>
              <div className="pf-form-row">
                <div className="pf-field">
                  <label>Nom du produit *</label>
                  <input type="text" value={formData.nom}
                    onChange={e => handleNomChange(e.target.value)}
                    placeholder="Ex: Coca-Cola 50cl" required autoFocus/>
                </div>
                <div className="pf-field">
                  <label>Code produit *</label>
                  <input type="text" value={formData.codeProduit}
                    onChange={e => setFormData({...formData, codeProduit: e.target.value})}
                    placeholder="Ex: PROD-001" required/>
                </div>
                <div className="pf-field">
                  <label>Code-barres</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input type="text" value={formData.codeBarres}
                      onChange={e => setFormData({...formData, codeBarres: e.target.value})}
                      placeholder="Ex: 3760123…" style={{ flex: 1 }}/>
                    <button type="button"
                      onClick={() => setFormData({ ...formData, codeBarres: generateCodeBarres() })}
                      title="Générer un code-barres"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '7px', background: '#f8fafc', color: '#0f172a', fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <Barcode size={14}/> Générer
                    </button>
                  </div>
                </div>
              </div>

              {/* Section: Tarification */}
              <div className="pf-section-label">Tarification <span className="pf-section-label-sub">(prix unitaire de base)</span></div>
              <div className="pf-form-row">
                <div className="pf-field">
                  <label>Prix achat (XOF) *</label>
                  <input type="number" min="0" step="0.01" required value={formData.prixAchatDetail}
                    onChange={e => setFormData({...formData, prixAchatDetail: e.target.value})} placeholder="0"/>
                </div>
                <div className="pf-field">
                  <label>Prix vente (XOF) *</label>
                  <input type="number" min="0" step="0.01" required value={formData.prixVenteDetail}
                    onChange={e => setFormData({...formData, prixVenteDetail: e.target.value})} placeholder="0"/>
                </div>
              </div>

              {/* Tarifs par boutique */}
              {!editingProduit && formData.boutiqueIdModal === 'all' && boutiques.length > 0 && (
                <div className="pf-field pf-field--full">
                  <div className="pf-price-table-head">
                    <label style={{margin:0}}>Tarifs par boutique <span className="pf-opt">(personnalisez par boutique)</span></label>
                    <button type="button" className="pf-apply-all-btn" onClick={applyDefaultPriceToAll}>
                      ↺ Appliquer le prix par défaut à toutes
                    </button>
                  </div>
                  <div className="pf-price-table-wrap">
                    <table className="pf-price-table">
                      <thead>
                        <tr>
                          <th>Boutique</th>
                          <th>Prix achat</th>
                          <th>Prix vente</th>
                        </tr>
                      </thead>
                      <tbody>
                        {boutiques.map(b => {
                          const bp = boutiquePrices[b._id] || {};
                          return (
                            <tr key={b._id}>
                              <td><strong>{b.nom}</strong></td>
                              <td><input type="number" min="0" step="1" value={bp.prixAchatDetail||''} onChange={e => setBoutiquePrice(b._id,'prixAchatDetail',e.target.value)} placeholder="0" required/></td>
                              <td><input type="number" min="0" step="1" value={bp.prixVenteDetail||''} onChange={e => setBoutiquePrice(b._id,'prixVenteDetail',e.target.value)} placeholder="0" required/></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Section: Conditionnements ── */}
              <div className="pf-section-label">
                Conditionnements
                <span className="pf-section-label-sub"> — Unité, Boîte, Caisse… (optionnel)</span>
              </div>
              <div className="pf-cond-wrap">
                {/* Liste des conditionnements existants */}
                {formData.conditionnements.length > 0 && (
                  <table className="pf-cond-table">
                    <thead>
                      <tr><th>Nom</th><th>Qté base</th><th>Prix achat</th><th>Prix vente</th><th></th></tr>
                    </thead>
                    <tbody>
                      {formData.conditionnements.map((c, i) => (
                        <tr key={i}>
                          <td><strong>{c.nom}</strong></td>
                          <td>{c.quantiteBase} unité{c.quantiteBase > 1 ? 's' : ''}</td>
                          <td>{c.prixAchat || '—'}</td>
                          <td>{c.prixVente}</td>
                          <td>
                            <button type="button" className="pf-ico-btn pf-ico-btn--del"
                              onClick={() => setFormData(f => ({ ...f, conditionnements: f.conditionnements.filter((_, j) => j !== i) }))}>
                              <Trash2 size={12}/>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Formulaire ajout conditionnement */}
                <div className="pf-cond-add">
                  <div className="pf-field">
                    <label>Nom <span className="pf-opt">ex: Boîte</span></label>
                    <input type="text" placeholder="Boîte, Caisse, Pack…"
                      value={newCond.nom} onChange={e => setNewCond({...newCond, nom: e.target.value})}/>
                  </div>
                  {(() => {
                    // Prix unitaire de référence : déduit du 1er conditionnement existant
                    // si présent, sinon du prix produit (formData)
                    const refCond  = formData.conditionnements[0];
                    const unitAchat = refCond && refCond.quantiteBase > 0
                      ? (Number(refCond.prixAchat) || 0) / Number(refCond.quantiteBase)
                      : (parseFloat(formData.prixAchatDetail || formData.prixAchat || 0) || 0);
                    const unitVente = refCond && refCond.quantiteBase > 0
                      ? (Number(refCond.prixVente) || 0) / Number(refCond.quantiteBase)
                      : (parseFloat(formData.prixVenteDetail || formData.prixVente || 0) || 0);
                    const q = parseInt(newCond.quantiteBase) || 0;
                    const autoAchat = (unitAchat > 0 && q > 0) ? Math.round(q * unitAchat) : null;
                    const autoVente = (unitVente > 0 && q > 0) ? Math.round(q * unitVente) : null;
                    return (
                      <>
                        <div className="pf-field">
                          <label>Qté base</label>
                          <input type="number" min="2" placeholder="50"
                            value={newCond.quantiteBase}
                            onChange={e => {
                              const qB = e.target.value;
                              const qq = parseInt(qB) || 0;
                              setNewCond(prev => ({
                                ...prev,
                                quantiteBase: qB,
                                // Recalcul à CHAQUE frappe tant que l'utilisateur n'a pas modifié manuellement
                                prixAchat: !prev.prixAchatManual && qq > 0 && unitAchat > 0
                                  ? String(Math.round(qq * unitAchat))
                                  : prev.prixAchat,
                                prixVente: !prev.prixVenteManual && qq > 0 && unitVente > 0
                                  ? String(Math.round(qq * unitVente))
                                  : prev.prixVente,
                              }));
                            }}/>
                        </div>
                        <div className="pf-field">
                          <label>Prix achat
                            {autoAchat != null && <span className="pf-opt"> auto : {autoAchat}</span>}
                          </label>
                          <input type="number" min="0" placeholder={autoAchat != null ? String(autoAchat) : '0'}
                            value={newCond.prixAchat}
                            onChange={e => setNewCond({...newCond, prixAchat: e.target.value, prixAchatManual: true})}/>
                        </div>
                        <div className="pf-field">
                          <label>Prix vente *
                            {autoVente != null && <span className="pf-opt"> auto : {autoVente}</span>}
                          </label>
                          <input type="number" min="0" placeholder={autoVente != null ? String(autoVente) : '0'}
                            value={newCond.prixVente}
                            onChange={e => setNewCond({...newCond, prixVente: e.target.value, prixVenteManual: true})}/>
                        </div>
                      </>
                    );
                  })()}
                  <button type="button" className="pf-cond-add-btn"
                    disabled={!newCond.nom.trim() || !newCond.quantiteBase || !newCond.prixVente}
                    onClick={() => {
                      setFormData(f => ({
                        ...f,
                        conditionnements: [...f.conditionnements, {
                          nom: newCond.nom.trim(),
                          quantiteBase: parseInt(newCond.quantiteBase),
                          prixVente: parseFloat(newCond.prixVente),
                          prixAchat: parseFloat(newCond.prixAchat) || 0,
                        }]
                      }));
                      setNewCond({ nom:'', quantiteBase:'', prixVente:'', prixAchat:'', prixAchatManual:false, prixVenteManual:false });
                    }}>
                    <Plus size={13}/> Ajouter
                  </button>
                </div>
              </div>

              {/* Section: Stock */}
              <div className="pf-section-label">Stock</div>
              <div className="pf-form-row">
                <div className="pf-field">
                  <label>Quantité initiale</label>
                  <input type="number" min="0" value={formData.quantiteStock}
                    onChange={e => setFormData({...formData, quantiteStock: e.target.value})} placeholder="0"/>
                </div>
                <div className="pf-field">
                  <label>Seuil d'alerte</label>
                  <input type="number" min="0" value={formData.seuilAlerte}
                    onChange={e => setFormData({...formData, seuilAlerte: e.target.value})} placeholder="10"/>
                </div>
                <div className="pf-field">
                  <label>Fournisseur</label>
                  <input type="text" value={formData.fournisseur}
                    onChange={e => setFormData({...formData, fournisseur: e.target.value})} placeholder="Nom du fournisseur"/>
                </div>
              </div>

              {/* Section: Infos */}
              <div className="pf-section-label">Infos complémentaires</div>
              <div className="pf-field pf-field--full">
                <label>Description</label>
                <textarea rows="2" value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Description optionnelle…"/>
              </div>

              {/* Section: Champs personnalisés */}
              <div className="pf-section-label">Champs personnalisés</div>
              {formData.champsPersonnalises.length > 0 && (
                <div className="pf-custom-fields-list">
                  {formData.champsPersonnalises.map((ch, idx) => (
                    <div key={idx} className="pf-custom-field-row">
                      <input
                        type="text"
                        className="pf-custom-field-key"
                        value={ch.cle}
                        placeholder="Libellé"
                        onChange={e => {
                          const updated = formData.champsPersonnalises.map((c, i) => i === idx ? { ...c, cle: e.target.value } : c);
                          setFormData({ ...formData, champsPersonnalises: updated });
                        }}
                      />
                      <span className="pf-custom-field-sep">:</span>
                      <input
                        type="text"
                        className="pf-custom-field-val"
                        value={ch.valeur}
                        placeholder="Valeur"
                        onChange={e => {
                          const updated = formData.champsPersonnalises.map((c, i) => i === idx ? { ...c, valeur: e.target.value } : c);
                          setFormData({ ...formData, champsPersonnalises: updated });
                        }}
                      />
                      <button type="button" className="pf-custom-field-del" onClick={() =>
                        setFormData({ ...formData, champsPersonnalises: formData.champsPersonnalises.filter((_, i) => i !== idx) })
                      }><X size={12}/></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="pf-cond-add-row">
                <input
                  type="text"
                  className="pf-custom-field-key"
                  placeholder="Libellé (ex: Couleur, Taille…)"
                  value={newChamp.cle}
                  onChange={e => setNewChamp({ ...newChamp, cle: e.target.value })}
                />
                <input
                  type="text"
                  className="pf-custom-field-val"
                  placeholder="Valeur"
                  value={newChamp.valeur}
                  onChange={e => setNewChamp({ ...newChamp, valeur: e.target.value })}
                />
                <button type="button" className="pf-cond-add-btn" disabled={!newChamp.cle.trim()}
                  onClick={() => {
                    setFormData(prev => ({ ...prev, champsPersonnalises: [...prev.champsPersonnalises, { cle: newChamp.cle.trim(), valeur: newChamp.valeur }] }));
                    setNewChamp({ cle: '', valeur: '' });
                  }}>
                  <Plus size={13}/> Ajouter
                </button>
              </div>

              {/* Image */}
              <div className="pf-field pf-field--full">
                <label>Image du produit</label>
                {imagePreview ? (
                  <div className="pf-img-preview-wrap">
                    <img src={imagePreview} alt="Preview" className="pf-img-preview"/>
                    <button type="button" className="pf-img-remove" onClick={handleRemoveImage}><X size={14}/></button>
                  </div>
                ) : (
                  <div className="pf-img-row">
                    <div className="pf-img-dropzone" onClick={() => document.getElementById('pf-img-input').click()}>
                      <Upload size={20}/>
                      <span>Télécharger une image</span>
                      <small>PNG, JPG, GIF · max 5 MB</small>
                      <input id="pf-img-input" type="file" accept="image/*" style={{display:'none'}} onChange={handleImageSelect}/>
                    </div>
                    <button type="button" className="pf-library-btn" onClick={loadImageLibrary}>
                      <ImageIcon size={13}/> Bibliothèque
                    </button>
                  </div>
                )}
                {uploadingImage && (
                  <div className="pf-uploading"><Loader size={13} className="pf-spin"/> Upload en cours…</div>
                )}
              </div>

              <div className="pf-modal-footer">
                <button type="button" className="pf-btn-cancel" onClick={() => { setShowModal(false); resetForm(); }}>Annuler</button>
                <button type="submit" className="pf-btn-confirm" disabled={submitting}>
                  {submitting ? <><Loader size={13} className="pf-spin"/> {editingProduit ? 'Modification…' : 'Création…'}</> : <><Save size={13}/> {editingProduit ? 'Enregistrer' : 'Créer le produit'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════
          Modal Vue produit
         ══════════════════════════════════════ */}
      {viewProduit && (() => {
        const p = viewProduit;
        const isLow  = p.quantiteStock <= p.seuilAlerte;
        const prix   = p.typeVente === 'gros' ? (p.prixVenteGros  || p.prixVente || 0) : (p.prixVenteDetail || p.prixVente || 0);
        const achat  = p.typeVente === 'gros' ? (p.prixAchatGros  || p.prixAchat || 0) : (p.prixAchatDetail || p.prixAchat || 0);
        const marge  = prix > 0 ? Math.round(((prix - achat) / prix) * 100) : 0;
        return (
          <>
            <div className="pf-overlay" onClick={() => setViewProduit(null)}/>
            <div className="pf-view-modal">
              <div className="pf-modal-head">
                <div>
                  <h2 className="pf-modal-title"><Eye size={15}/> Fiche produit</h2>
                  <p className="pf-modal-sub">{p.codeProduit}</p>
                </div>
                <div className="pf-view-head-actions">
                  <button className="pf-ico-btn pf-ico-btn--edit" onClick={() => { setViewProduit(null); handleEditClick(p); }} title="Modifier">
                    <Edit2 size={13}/>
                  </button>
                  <button className="pf-modal-close" onClick={() => setViewProduit(null)}><X size={16}/></button>
                </div>
              </div>

              <div className="pf-view-body">
                {/* Image + identité */}
                <div className="pf-view-hero">
                  {p.image
                    ? <img src={p.image} alt={p.nom}
                        className="pf-view-img pf-view-img--clickable"
                        loading="lazy" decoding="async"
                        title="Cliquer pour agrandir"
                        onClick={() => setLightbox({ url: p.image, nom: p.nom, code: p.codeProduit })}
                        onError={e => e.target.style.display='none'}/>
                    : <div className="pf-view-img-placeholder"><Package size={36}/></div>}
                  <div className="pf-view-hero-info">
                    <h3 className="pf-view-nom">{p.nom}</h3>
                    <div className="pf-view-badges">
                      {p.conditionnements?.length > 0 && (
                        <span className="pf-type-badge pf-type-badge--detail">
                          {p.conditionnements.length} conditionnement{p.conditionnements.length > 1 ? 's' : ''}
                        </span>
                      )}
                      <span className={`pf-status-badge ${isLow ? 'pf-status-badge--low' : 'pf-status-badge--ok'}`}>
                        {isLow ? 'Stock bas' : 'Disponible'}
                      </span>
                    </div>
                    {p.description && <p className="pf-view-desc">{p.description}</p>}
                  </div>
                </div>

                {/* Grille de détails */}
                <div className="pf-view-grid">

                  <div className="pf-view-section">
                    <div className="pf-view-section-title"><Hash size={12}/> Identification</div>
                    <div className="pf-view-items">
                      <div className="pf-view-item"><span>Code produit</span><strong>{p.codeProduit}</strong></div>
                      {p.codeBarres && <div className="pf-view-item"><span><Barcode size={11}/> Code-barres</span><strong className="pf-barcode-cell">{p.codeBarres}</strong></div>}
                      {p.fournisseur && <div className="pf-view-item"><span>Fournisseur</span><strong>{p.fournisseur}</strong></div>}
                    </div>
                  </div>

                  <div className="pf-view-section">
                    <div className="pf-view-section-title"><Tag size={12}/> Tarification</div>
                    <div className="pf-view-items">
                      <div className="pf-view-item"><span>Prix achat</span><strong className="pf-price--achat">{fmt(achat)}</strong></div>
                      <div className="pf-view-item"><span>Prix vente</span><strong>{fmt(prix)}</strong></div>
                      <div className="pf-view-item">
                        <span>Marge</span>
                        <strong style={{ color: marge >= 20 ? '#16a34a' : marge >= 10 ? '#d97706' : '#dc2626' }}>
                          {marge} %
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="pf-view-section">
                    <div className="pf-view-section-title"><Warehouse size={12}/> Stock</div>
                    <div className="pf-view-items">
                      <div className="pf-view-item">
                        <span>Quantité</span>
                        <strong style={{ color: isLow ? '#dc2626' : '#16a34a' }}>
                          {p.quantiteStock} unité{p.quantiteStock > 1 ? 's' : ''}
                        </strong>
                      </div>
                      <div className="pf-view-item"><span>Seuil d'alerte</span><strong>{p.seuilAlerte} u</strong></div>
                    </div>
                  </div>

                </div>

                {/* Description complète si longue */}
                {p.description && p.description.length > 80 && (
                  <div className="pf-view-section pf-view-section--full">
                    <div className="pf-view-section-title"><AlignLeft size={12}/> Description</div>
                    <p className="pf-view-long-desc">{p.description}</p>
                  </div>
                )}

                {/* Champs personnalisés */}
                {p.champsPersonnalises?.length > 0 && (
                  <div className="pf-view-section pf-view-section--full">
                    <div className="pf-view-section-title"><Tag size={12}/> Champs personnalisés</div>
                    <div className="pf-view-items">
                      {p.champsPersonnalises.map((ch, i) => (
                        <div key={i} className="pf-view-item">
                          <span>{ch.cle}</span>
                          <strong>{ch.valeur || '—'}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="pf-modal-footer">
                <button className="pf-btn-cancel" onClick={() => setViewProduit(null)}>Fermer</button>
                <button className="pf-btn-confirm" onClick={() => { setViewProduit(null); handleEditClick(p); }}>
                  <Edit2 size={13}/> Modifier
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Modal Bibliothèque ── */}
      {showImageLibrary && (
        <>
          <div className="pf-overlay" onClick={() => setShowImageLibrary(false)}/>
          <div className="pf-modal pf-modal--wide">
            <div className="pf-modal-head">
              <div><h2 className="pf-modal-title">Bibliothèque d'images</h2></div>
              <button className="pf-modal-close" onClick={() => setShowImageLibrary(false)}><X size={16}/></button>
            </div>
            <div className="pf-modal-body">
              {imageLibrary.length === 0 ? (
                <div className="pf-empty"><div className="pf-empty-icon"><ImageIcon size={24}/></div><p>Aucune image</p></div>
              ) : (
                <div className="pf-library-grid">
                  {imageLibrary.map((img, i) => (
                    <div key={i} className="pf-library-item" onClick={() => selectImageFromLibrary(img.url)}>
                      <img src={img.url} alt={img.filename} loading="lazy" decoding="async"/>
                      <span>{img.filename}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Modal Distribution Stock ── */}
      {showStockModal && selectedProduitStock && (
        <>
          <div className="pf-overlay" onClick={closeStockModal}/>
          <div className="pf-modal">
            <div className="pf-modal-head">
              <div>
                <h2 className="pf-modal-title">Distribution du stock</h2>
                <p className="pf-modal-sub">{selectedProduitStock.nom} · {selectedProduitStock.codeProduit}</p>
              </div>
              <button className="pf-modal-close" onClick={closeStockModal}><X size={16}/></button>
            </div>
            <div className="pf-modal-body">
              {loadingDistribution ? (
                <div className="pf-dist-loading"><Loader size={24} className="pf-spin"/><span>Chargement…</span></div>
              ) : stockDistribution ? (
                <>
                  <div className="pf-dist-total">
                    <span>Stock total</span>
                    <strong>{stockDistribution.stockTotalCalcule} unités</strong>
                  </div>
                  {stockDistribution.distribution?.length > 0 ? (
                    <table className="pf-table pf-table--compact">
                      <thead><tr><th>Entrepôt</th><th>Adresse</th><th style={{textAlign:'right'}}>Qté</th><th>Statut</th></tr></thead>
                      <tbody>
                        {stockDistribution.distribution.map((item, i) => (
                          <tr key={i}>
                            <td><strong>{item.entrepot.nom}</strong></td>
                            <td className="pf-muted">{item.entrepot.adresse || '—'}</td>
                            <td style={{textAlign:'right', fontWeight:600}}>{item.quantiteStock} u</td>
                            <td>
                              <span className={`pf-status-badge ${item.entrepot.actif ? 'pf-status-badge--ok' : 'pf-status-badge--low'}`}>
                                {item.entrepot.actif ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                          </tr>
                        ))}
                        <tr className="pf-dist-total-row">
                          <td colSpan="2"><strong>TOTAL</strong></td>
                          <td style={{textAlign:'right'}}><strong>{stockDistribution.distribution.reduce((s,i) => s+i.quantiteStock,0)} u</strong></td>
                          <td/>
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <div className="pf-empty"><p>Aucune distribution trouvée</p></div>
                  )}
                </>
              ) : (
                <div className="pf-empty"><AlertCircle size={24} style={{color:'#ef4444'}}/><p>Erreur de chargement</p></div>
              )}
              <div className="pf-modal-footer">
                <button className="pf-btn-cancel" onClick={closeStockModal}>Fermer</button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Modal Import Excel ── */}
      {showImportModal && (
        <>
          <div className="pf-overlay" onClick={() => !importing && setShowImportModal(false)}/>
          <div className="pf-modal pf-modal--wide">
            <div className="pf-modal-head">
              <div>
                <h2 className="pf-modal-title">Importer des produits</h2>
                <p className="pf-modal-sub">Une ligne par produit par boutique — prix différents possibles par boutique</p>
              </div>
              <button className="pf-modal-close" onClick={() => setShowImportModal(false)} disabled={importing}><X size={16}/></button>
            </div>
            <div className="pf-modal-body">

              {!importResult && (
                <>
                  <div className="pf-import-info">
                    <AlertCircle size={13}/>
                    <span>Colonnes obligatoires : <strong>Nom</strong>, <strong>Prix Achat</strong>, <strong>Prix Vente</strong>. Pour des prix différents par boutique, répéter le produit avec le nom de chaque boutique.</span>
                    <button className="pf-template-btn" onClick={downloadTemplate}><Download size={11}/> Modèle</button>
                  </div>
                  <div className="pf-dropzone" onClick={() => importFileRef.current?.click()}>
                    <Upload size={26} className="pf-dropzone-ico"/>
                    <p>Cliquer pour choisir un fichier Excel</p>
                    <span>.xlsx ou .xls</span>
                    <input ref={importFileRef} type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={handleImportFileChange}/>
                  </div>
                </>
              )}

              {importError && (
                <div className="pf-import-error"><AlertCircle size={13}/> {importError}</div>
              )}

              {importPreview.length > 0 && !importResult && (
                <div className="pf-preview">
                  <div className="pf-preview-label">Aperçu — {importPreview.length} ligne{importPreview.length !== 1 ? 's' : ''}</div>
                  <div className="pf-preview-scroll">
                    <table className="pf-preview-table">
                      <thead><tr><th>Image</th><th>Nom</th><th>Boutique</th><th>Prix achat</th><th>Prix vente</th><th>Stock</th></tr></thead>
                      <tbody>
                        {importPreview.map((r,i) => (
                          <tr key={i}>
                            <td>{r.imageUrl ? <img src={r.imageUrl} alt="" className="pf-import-thumb" loading="lazy" decoding="async" onError={e => e.target.style.display='none'}/> : <span className="pf-muted">—</span>}</td>
                            <td><strong>{r.nom}</strong></td>
                            <td>{r.boutique ? <span className="pf-boutique-pill">{r.boutique}</span> : <span className="pf-muted">Toutes</span>}</td>
                            <td>{r.prixAchat.toLocaleString()}</td>
                            <td><strong>{r.prixVente.toLocaleString()}</strong></td>
                            <td>{r.stock}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {importResult && (
                <div className="pf-import-result">
                  <CheckCircle size={36} className="pf-import-result-ico"/>
                  <p className="pf-import-result-title">Import terminé</p>
                  <div className="pf-import-stats">
                    {[
                      { val: importResult.created, label: 'produits créés', color: '#10b981' },
                      { val: importResult.skipped, label: 'déjà existants', color: '#94a3b8' },
                      ...(importResult.errors.length ? [{ val: importResult.errors.length, label: 'erreurs', color: '#ef4444' }] : []),
                    ].map((s,i) => (
                      <div key={i} className="pf-import-stat" style={{ borderTopColor: s.color }}>
                        <span className="pf-import-stat-val" style={{ color: s.color }}>{s.val}</span>
                        <span className="pf-import-stat-lab">{s.label}</span>
                      </div>
                    ))}
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="pf-import-errors">
                      <p>Erreurs :</p>
                      <ul>{importResult.errors.map((e,i) => <li key={i}>{e}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}

              {importing && importProgress.total > 0 && (
                <div className="pf-import-progress">
                  <div className="pf-import-progress-label">
                    <Loader size={13} className="pf-spin"/>
                    <span>{importProgress.step}</span>
                    {importProgress.done > 0 && <span className="pf-import-progress-count">{importProgress.done}/{importProgress.total}</span>}
                  </div>
                  <div className="pf-import-progress-bar">
                    <div className="pf-import-progress-fill" style={{ width: `${Math.round((importProgress.done / importProgress.total) * 100)}%` }}/>
                  </div>
                </div>
              )}

              <div className="pf-modal-footer">
                {!importResult ? (
                  <>
                    <button className="pf-btn-cancel" onClick={() => setShowImportModal(false)} disabled={importing}>Annuler</button>
                    <button className="pf-btn-confirm" onClick={handleImport} disabled={importing || !importPreview.length}>
                      {importing ? <><Loader size={13} className="pf-spin"/> Traitement…</> : <><Upload size={13}/> Importer {importPreview.length > 0 ? `(${importPreview.length})` : ''}</>}
                    </button>
                  </>
                ) : (
                  <button className="pf-btn-confirm" onClick={() => setShowImportModal(false)}>Fermer</button>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ──────────── Lightbox : image produit en plein écran ──────────── */}
      {lightbox && (
        <div className="pf-lightbox" onClick={() => setLightbox(null)} role="dialog" aria-modal="true">
          <button type="button" className="pf-lightbox-close" onClick={() => setLightbox(null)} aria-label="Fermer">
            <X size={22}/>
          </button>
          <figure className="pf-lightbox-fig" onClick={e => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.nom} className="pf-lightbox-img"/>
            <figcaption className="pf-lightbox-caption">
              <span className="pf-lightbox-nom">{lightbox.nom}</span>
              {lightbox.code && <span className="pf-lightbox-code">{lightbox.code}</span>}
            </figcaption>
          </figure>
        </div>
      )}
    </div>
  );
};

export default ProduitsFeveo;
